import { Component } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from './shared/components/layout/header/header.component';
import { SidebarComponent } from './shared/components/layout/sidebar/sidebar.component';
import { BillingStatusService } from './core/services/billing-status.service';
import { shouldShowNotice } from './core/utils/billing-restriction.utils';
import { BillingNoticeComponent } from './shared/components/billing-notice/billing-notice.component';
import { ExportRequestModalComponent } from './shared/components/export-request-modal/export-request-modal.component';
import { PermissionNoticeComponent } from './shared/components/permission-notice/permission-notice.component';
import { PermissionDenialService } from './core/services/permission-denial.service';
import { filter } from 'rxjs/operators';
import { StoreContextService } from './core/services/store-context.service';
import { ExportSwitchesService } from './core/services/export-switches.service';
import { VersionCheckerService } from './core/services/version-checker.service'; // v1.6.538

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, HeaderComponent, SidebarComponent, ExportRequestModalComponent, BillingNoticeComponent, PermissionNoticeComponent],
  template: `
    <div class="min-h-screen bg-gray-50">
      <app-header *ngIf="!isAuthPage"></app-header>
      <app-sidebar *ngIf="!isAuthPage"></app-sidebar>
      <!-- ══ v1.6.1766 — ONE header offset (Asana 1219657717405746) ══

           Angelo: "Every page shows this extra blank space above the page
           title that I believe is meant for the error messages, except it's
           showing as extra blank space even when the error messages aren't
           there."

           He was right about the cause. The offset for the FIXED 64px header
           used to be split between two elements, and both were applied at
           once on a healthy tenant:

             • the billing notice's host carried "pt-16" UNCONDITIONALLY —
               "*ngIf" only governs its CONTENT, so a host with no content
               still painted 64px of padding;
             • "<main>" then added "mt-16" because the notice was "not
               visible";
             • and the permission notice's host carried "mb-4" the same way,
               always.

           64 + 64 + 16 = 144px of offset where 64px was wanted.

           The split also had a second bug: "billingNoticeVisible" tracks only
           "shouldShowNotice", while the notice renders for THREE states
           (that one, the grace note, and a failed status read). In the other
           two it painted a banner AND <main> still added its 64px.

           Now the offset is ONE wrapper that always clears the header, and
           neither notice contributes spacing of its own. Each one adds height
           only when it actually renders something. -->
      <div [class.pt-16]="!isAuthPage">
        <!-- v1.6.1538 — above the content so a mid-session block paints from
             any screen. Hidden on auth pages: login and logout stay reachable
             while restricted, and a lock notice on the login form would be
             nonsense. -->
        <app-billing-notice *ngIf="!isAuthPage" class="block md:ml-64"></app-billing-notice>
        <main [class]="isAuthPage ? '' : 'md:ml-64 p-4 md:p-6 pb-24 md:pb-6'">
          <!-- v1.6.1687 — Asana 1216892546346406. One notice for every
               permission 403 on this page, mounted once so no screen needs its
               own markup. It renders nothing when there is nothing to say, and
               sits INSIDE <main> so it never shifts the layout the way the
               billing notice (a full-width lock-out) has to.

               v1.6.1766 — its "mb-4" moved INTO its own template, so the gap
               exists only when there is a notice to separate from the page. -->
          <app-permission-notice *ngIf="!isAuthPage" class="block"></app-permission-notice>
          <router-outlet></router-outlet>
        </main>
      </div>
      <app-export-request-modal></app-export-request-modal>
    </div>
  `
})
export class AppComponent {
  isAuthPage = true;
  /**
   * v1.6.1538 — was the layout's switch for where the header offset went.
   *
   * v1.6.1766 — it no longer drives any spacing: the offset is one wrapper
   * that always clears the fixed header, so nothing has to predict whether
   * the notice will paint. Kept because it also tracked only ONE of the
   * notice's three visible states, which is what made it wrong in the first
   * place — a layout decision made outside the component that owns it.
   *
   * The subscription stays: it is the app shell's observer on the billing
   * status, and dropping it would change when that status is first read.
   */
  billingNoticeVisible = false;

  // v1.6.776 — Include '/auth/impersonate' so the header/sidebar do
  // NOT mount on the super-admin impersonation landing. Pre-fix they
  // mounted and fired their init HTTP calls (store list, etc.) BEFORE
  // ImpersonateComponent had a chance to install the bearer token,
  // producing a 401 that the api-log interceptor then translated into
  // a redirect to /login — making the impersonation URL only work
  // when an operator was *already* logged in.
  private authRoutes = ['/login', '/reset-password', '/forgot-password', '/select-instance', '/verify-otp', '/dev', '/auth/impersonate'];

  private storeContextLoaded = false;
  private lastActivityTime = Date.now();

  constructor(
    private router: Router, 
    private storeCtx: StoreContextService,
    private versionChecker: VersionCheckerService, // v1.6.538
    // v1.6.1538 — APPENDED LAST (HANDOFF §2).
    private billing: BillingStatusService,
    // v1.6.1687 — APPENDED LAST.
    private permissionDenials: PermissionDenialService,
    // v1.6.1814 — APPENDED LAST (Asana 1219882772481516). Every existing
    // `new AppComponent(...)` in the specs keeps its argument order.
    private exportSwitches: ExportSwitchesService,
  ) {
    // v1.6.1538 — the notice carries the header offset when it is showing, so
    // <main> needs one only while it is absent.
    this.billing.status$.subscribe(st => {
      this.billingNoticeVisible = shouldShowNotice(st);
    });
    // v1.6.538 — Start automatic version checking
    this.versionChecker.startChecking();
    
    // v1.6.477 — Track user activity to detect idle state
    this.setupActivityTracking();

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        const url = event.urlAfterRedirects || event.url;
        
        // v1.6.477 — Log navigation for debugging idle navigation issues
        const idleTime = (Date.now() - this.lastActivityTime) / 1000;
        if (idleTime > 300) { // 5+ minutes idle
          console.warn(`[Navigation] Navigated to ${url} after ${Math.round(idleTime)}s idle`);
        }
        
        this.isAuthPage = this.authRoutes.some(route => url === route || url.startsWith(route + '?'));
        /**
         * v1.6.1687 — denials are PER PAGE. A missing `order.viewAny`
         * collected on Orders says nothing about Products, and leaving it
         * would banner a page that works perfectly.
         *
         * Cleared on NavigationEnd, i.e. before the new page's requests go
         * out, so the notice only ever reflects the screen in front of the
         * operator.
         */
        this.permissionDenials.clear();
        // Scroll to top on every navigation
        window.scrollTo({ top: 0, behavior: 'instant' });
        // Load store context once after first non-auth navigation
        if (!this.isAuthPage && !this.storeContextLoaded) {
          this.storeContextLoaded = true;
          this.storeCtx.load();
          // v1.6.1814 — Asana 1219882772481516: "load GET api/v1/tenant/info
          // once after sign in and keep is_general_export_enabled and
          // is_report_export_enabled for the whole panel". Loaded in the same
          // place and the same way as the store context, so every export
          // button can read the switches synchronously instead of each screen
          // fetching them. The call is idempotent and a failure is a no-op.
          this.exportSwitches.load().subscribe({ error: () => {} });
        }
        
        // Update activity time on navigation
        this.lastActivityTime = Date.now();
      });
  }

  // v1.6.477 — Track user activity to detect idle periods
  private setupActivityTracking(): void {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, () => {
        this.lastActivityTime = Date.now();
      }, { passive: true });
    });
  }
}
